// File: RoomCheckOut.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

import {
  getAllChecInRoom,
  getAllPaymentMethods,
  GetParKingBillByID,
  GetPoolBookingBillByID,
  Search,
  Update,
  DeleteParking,
  DeletePoolbooking,
  GetTransportVehicleBillByID,
  DeleteTransport
} from "adminApi/RoomCheCkOutApi/RoomCheCkOut";

import { RoomCheckOutRules } from "validation/ValidationRules";
import { validateForm } from "utils/validation";
import Spinner from "Common/Globle/Spinner";
import RoomCheckOutForm from "./Componates/RoomCheckOutForm";
import RoomCheckOutTable from "./Componates/RoomCheckOutTable";

import fields from "./json/RoomCheckOutFields.json";
import useFormChange from "Hooks/useFormChange";
import useFetchAllData from "Hooks/useFetchAllData";
import useFormSubmit from "Hooks/useFormSubmit";

const RoomCheckOut = () => {
  const { formdata } = fields;
  const location = useLocation();

  /* -------------------- STATE -------------------- */
  const [searchData, setSearchData] = useState(null);
  const [RoomsOpction, setRoomsOpction] = useState([]);

  const [poolBills, setPoolBills] = useState([]);
  const [parkingBills, setParkingBills] = useState([]);
  const [transportBills, setTransportBills] = useState([]);

  const [poolLoading, setPoolLoading] = useState(false);
  const [parkingLoading, setParkingLoading] = useState(false);
  const [transportLoading, setTransportLoading] = useState(false);

  const [refetchFlag, setRefetchFlag] = useState(false);
  const refetch = () => setRefetchFlag(prev => !prev);

  /* -------------------- FORM -------------------- */
  const initialValues = formdata.reduce(
    (acc, field) => ({ ...acc, [field.name]: "" }),
    {}
  );

  const { values, setValues, errors, handleChange, setErrors } = useFormChange(
    initialValues,
    RoomCheckOutRules
  );

  /* -------------------- MASTER DATA -------------------- */
  const {
    data: CheckInRoomsOpction = [],
    loading: CheckInRoomsOpctionLoading,
    refetch : RCheckInRoomOption
  } = useFetchAllData(getAllChecInRoom);

  // getAllPaymentMethods returns { data: { success, data: [...] } } which matches useFetchAllData
  const {
    data: paymentMethodTypesOption = [],
    loading: paymentMethodTypesOptionLoading
  } = useFetchAllData(getAllPaymentMethods);

  /* -------------------- SEARCH DATA -------------------- */
  const customer = searchData?.customer_details?.[0] || null;

  /* -------------------- POOL BILLS -------------------- */
  useEffect(() => {
    if (!customer?.customer_id) return;

    let mounted = true;

    const fetchPoolBills = async () => {
      setPoolLoading(true);
      try {
        const res = await GetPoolBookingBillByID({
          customer_id: customer.customer_id,
          check_in_date: customer.check_in_date,
          check_out_date: customer.check_out_date
        });
        mounted && setPoolBills(res?.data?.data || []);
      } catch (err) {
        console.error("Pool booking fetch error", err);
      } finally {
        mounted && setPoolLoading(false);
      }
    };

    fetchPoolBills();
    return () => { mounted = false; };
  }, [
    customer?.customer_id,
    customer?.check_in_date,
    customer?.check_out_date,
    refetchFlag
  ]);

  /* -------------------- PARKING BILLS -------------------- */
  useEffect(() => {
    if (!customer?.customer_id) return;

    let mounted = true;

    const fetchParkingBills = async () => {
      setParkingLoading(true);
      try {
        const res = await GetParKingBillByID({
          customer_id: customer.customer_id,
          check_in_date: customer.check_in_date,
          check_out_date: customer.check_out_date
        });
        mounted && setParkingBills(res?.data?.data || []);
      } catch (err) {
        console.error("Parking booking fetch error", err);
      } finally {
        mounted && setParkingLoading(false);
      }
    };

    fetchParkingBills();
    return () => { mounted = false; };
  }, [
    customer?.customer_id,
    customer?.check_in_date,
    customer?.check_out_date,
    refetchFlag
  ]);

  /* -------------------- TRANSPORT BILLS -------------------- */
  useEffect(() => {
    if (!customer?.customer_id) return;

    let mounted = true;

    const fetchTransportBills = async () => {
      setTransportLoading(true);
      try {
        const res = await GetTransportVehicleBillByID({
          customer_id: customer.customer_id,
          check_in_date: customer.check_in_date,
          check_out_date: customer.check_out_date
        });
        mounted && setTransportBills(res?.data?.data || []);
      } catch (err) {
        console.error("Transport booking fetch error", err);
      } finally {
        mounted && setTransportLoading(false);
      }
    };

    fetchTransportBills();
    return () => { mounted = false; };
  }, [
    customer?.customer_id,
    customer?.check_in_date,
    customer?.check_out_date,
    refetchFlag
  ]);

  /* -------------------- ROOM OPTIONS -------------------- */
  useEffect(() => {
    setRoomsOpction(CheckInRoomsOpction);
  }, [CheckInRoomsOpction, RCheckInRoomOption]);

  /* -------------------- SEARCH -------------------- */
  // FIX #1 & #2 & #8: use useCallback, read booking_id from values directly,
  // accept an optional id override to avoid stale closure issues
  const handleSearch = useCallback(async (bookingId) => {
    const id = bookingId || values.booking_id;
    if (!id) return;

    try {
      const res = await Search(id);
      setSearchData(res?.data?.data ?? null);
    } catch (err) {
      console.error("Search Error", err);
    }
  }, [values.booking_id]);

  // FIX #8: use location.state instead of localStorage for cross-route booking ID
  useEffect(() => {
    const stateBookingId = location.state?.booking_id;
    if (stateBookingId) {
      handleSearch(stateBookingId);
    }
  }, [location.state?.booking_id, handleSearch]);

  /* -------------------- SUBMIT -------------------- */
  const { loading: loadingSubmit, handleSubmit: postData } = useFormSubmit(
    async payload => {
      const formData = new FormData();

      /* ---------- JSON FIELDS ---------- */
      if (payload.parking_booking_id) {
        formData.append("parking_booking_id", JSON.stringify(payload.parking_booking_id));
      }
      if (payload.p_booking_id) {
        formData.append("p_booking_id", JSON.stringify(payload.p_booking_id));
      }
      if (payload.vehicle_booking_id) {
        formData.append("vehicle_booking_id", JSON.stringify(payload.vehicle_booking_id));
      }
      if (payload.settlements) {
        formData.append("settlements", JSON.stringify(payload.settlements));
      }

      /* ---------- NORMAL FIELDS ---------- */
      Object.entries(payload).forEach(([key, value]) => {
        if (["parking_booking_id", "p_booking_id", "vehicle_booking_id", "settlements"].includes(key))
          return;
        formData.append(key, value ?? "");
      });

      return Update({ id: payload.booking_id, data: formData });
    },
    // FIX #6: real validator instead of no-op
    (vals) => {
      const errs = validateForm(vals, RoomCheckOutRules);
      setErrors(errs);
      return errs;
    },
    () => {
      setSearchData(null);
      setPoolBills([]);
      setParkingBills([]);
      setTransportBills([]);
      setValues(prev => ({ ...prev, booking_id: "" }));
      RCheckInRoomOption()
    },
    "Room Checked Out Successfully"
  );

  /* -------------------- LOADING -------------------- */
  const isLoading =
    CheckInRoomsOpctionLoading ||
    paymentMethodTypesOptionLoading ||
    loadingSubmit ||
    poolLoading ||
    parkingLoading ||
    transportLoading;

  /* -------------------- RENDER -------------------- */
  return (
    <>
      {isLoading && <Spinner isSpinner />}

      <RoomCheckOutForm
        fields={formdata}
        values={values}
        errors={errors}
        handleChange={handleChange}
        CheckInRoomsOpction={RoomsOpction}
        handleSearch={() => handleSearch()}
        loading={CheckInRoomsOpctionLoading}
      />

      {searchData && (
        <RoomCheckOutTable
          RoomCkeckOutDetail={searchData}
          paymentMethodTypesOption={paymentMethodTypesOption}
          onCheckOut={postData}
          parkingBills={parkingBills}
          poolBills={poolBills}
          transportBills={transportBills}
          DeleteParking={DeleteParking}
          DeletePoolbooking={DeletePoolbooking}
          DeleteTransport={DeleteTransport}
          refetch={refetch}
        />
      )}
    </>
  );
};

export default RoomCheckOut;
